import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@fortawesome/pro-regular-svg-icons";
import Input from "components/form/input/Input";
import Checkbox from "components/form/checkbox/Checkbox";
import { Select } from "components/select/index";
import {
  TWebsiteBuilderOptionalLink,
  TWebsiteBuilderOptionalLinkType,
} from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderLink";
import { WEBSITE_BUILDER_PORTAL } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import { IWebsiteBuilderSelectOption, renderWebsiteBuilderSelectOptions } from "./renderWebsiteBuilderSelectOptions";

interface IWebsiteBuilderOptionalLinkSettingsProps {
  link: TWebsiteBuilderOptionalLink;
  onChange: (link: TWebsiteBuilderOptionalLink) => void;
  onDebouncedChange: (link: TWebsiteBuilderOptionalLink) => void;
}

export function WebsiteBuilderOptionalLinkSettings(props: IWebsiteBuilderOptionalLinkSettingsProps) {
  const { link, onChange, onDebouncedChange } = props;

  const { t } = useTranslation();

  const linkTypeOptions: IWebsiteBuilderSelectOption<TWebsiteBuilderOptionalLinkType>[] = useMemo(() => {
    return [
      {
        label: t("None"),
        value: "none",
      },
      {
        label: t("Url"),
        value: "url",
      },
      {
        label: t("Email"),
        value: "email",
      },
    ];
  }, []);

  const linkTypeSelect = 
    <Select
      label={t("Link Type")}
      defaultValue={link.type}
      onChange={(linkType: TWebsiteBuilderOptionalLinkType) => {
        if (linkType !== link.type) {
          let nextLink: TWebsiteBuilderOptionalLink = { type: "none" };
        
          if (linkType === "url") {
            nextLink = {
              type: "url",
              href: "",
              blankTarget: false,
            };
          } else if (linkType === "email") {
            nextLink = {
              type: "email",
              address: "",
              subject: "",
            };
          }

          onChange(nextLink);
        }
      }}
      usePortal
      portalId={WEBSITE_BUILDER_PORTAL}
    >
      {renderWebsiteBuilderSelectOptions(linkTypeOptions)}
    </Select>;

  if (link.type === "url") {
    return (
      <>
        {linkTypeSelect}
        <Input
          label={t("Link")}
          value={link.href}
          prefix={<FontAwesomeIcon icon={faLink} />}
          onChange={e =>
            onDebouncedChange({
              ...link,
              href: e.target.value,
            })
          }
        />
        <Checkbox
          size="medium"
          label={t("Open link in new tab")}
          checked={link.blankTarget}
          onChange={e =>
            onChange({
              ...link,
              blankTarget: e.target.checked,
            })
          }
        />
      </>
    );
  } else if (link.type === "email") {
    return (
      <>
        {linkTypeSelect}
        <Input
          label={t("Address")}
          value={link.address}
          prefix={<FontAwesomeIcon icon={faLink} />}
          onChange={e =>
            onDebouncedChange({
              ...link,
              address: e.target.value,
            })
          }
        />
        <Input
          label={t("Subject")}
          value={link.subject}
          onChange={e =>
            onDebouncedChange({
              ...link,
              subject: e.target.value,
            })
          }
        />
      </>
    );
  } else {
    return linkTypeSelect;
  }
}
